import { useMemo } from "react";
import {
  useV2ListProjectExpensesQuery,
  useV2ListProjectPricingsQuery,
  useV2ListProjectProductsForAccountQuery,
} from "@generated";
import { useListProjectServices } from "../../ProjectServicesV2/common/api";
import useListGovernances from "../../GovernanceV2/Show/api/useListGovernances";
import {
  pricingIndex,
  toChildGovernances,
  toChildScopeServices,
  type ChildGovernance,
  type ChildService,
} from "./childScope";
import {
  toChildExpenses,
  toChildProducts,
  type ChildExpense,
  type ChildProduct,
} from "./childExtras";

export interface ChildScope {
  professional: ChildService[];
  managed: ChildService[];
  governances: ChildGovernance[];
  expenses: ChildExpense[];
  products: ChildProduct[];
  isLoading: boolean;
  error: boolean;
  notFound: boolean;
  expensesError: boolean;
  productsError: boolean;
}

const is404 = (error: unknown) => (error as { status?: unknown } | null)?.status === 404;

// Everything on the change request project, for Show. Pricing only fills in
// revenue; expenses and products fail on their own section.
export const useChildScope = (
  childProjectId: string | null,
  options: { products: boolean }
): ChildScope => {
  const skip = !childProjectId;
  const skipProducts = skip || !options.products;
  const project_id = childProjectId ?? "";
  const ps = useListProjectServices({ project_id, service_type: "professional_services", isChangeOrderChild: true, skip });
  const ms = useListProjectServices({ project_id, service_type: "managed_services", isChangeOrderChild: true, skip });
  // project-phase must be included and phases/lobs passed, or the hook's
  // mapping throws (see useListGovernances).
  const gov = useListGovernances({
    page: { size: 500 },
    include: ["resource", "project-phase", "project-resource", "project", "service-category"],
    filter: { project: project_id, active: true },
    projectPhases: [],
    lobs: [],
    skip,
  } as any);
  const pricing = useV2ListProjectPricingsQuery({ projectId: project_id }, { skip });
  const expenses = useV2ListProjectExpensesQuery(
    { filter: { project: project_id, active: true }, include: ["expense-category"], page: { size: 500 } },
    { skip }
  );
  const products = useV2ListProjectProductsForAccountQuery(
    { filter: { project: project_id, active: true }, page: { size: 500 } },
    { skip: skipProducts }
  );

  return useMemo(() => {
    const index = pricingIndex(pricing.data?.data as any);
    return {
      professional: toChildScopeServices(ps.fetchedServices ?? [], index),
      managed: toChildScopeServices(ms.fetchedServices ?? [], index),
      governances: toChildGovernances(gov.data ?? [], index),
      expenses: toChildExpenses(expenses.data as any),
      products: skipProducts ? [] : toChildProducts(products.data?.data as any),
      // useListProjectServices reads as loading while skipped.
      isLoading:
        !skip &&
        Boolean(
          ps.servicesLoading || ms.servicesLoading || gov.governancesLoading ||
            expenses.isLoading || (!skipProducts && products.isLoading)
        ),
      error: Boolean(ps.error || ms.error || gov.governancesError),
      notFound: is404(ps.error) || is404(ms.error),
      expensesError: Boolean(expenses.error),
      productsError: !skipProducts && Boolean(products.error),
    };
  }, [skip, skipProducts, ps.fetchedServices, ms.fetchedServices, ps.servicesLoading, ms.servicesLoading,
      ps.error, ms.error, gov.data, gov.governancesLoading, gov.governancesError, pricing.data,
      expenses.data, expenses.isLoading, expenses.error, products.data, products.isLoading, products.error]);
};

export default useChildScope;
